import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteNav } from "@/components/site/SiteNav";
import { Footer } from "@/components/site/Sections";
import {
  Film,
  CheckCircle2,
  ArrowRight,
  ChevronRight,
  Sparkles,
  BarChart3,
  TrendingUp,
  Target,
  Users,
  Award,
  Globe,
  Clock,
  ShieldCheck,
  Check,
  Building2,
  FileCheck,
  Layers,
  Search,
  Tv,
  Eye,
  Camera,
  Ticket,
  Maximize2,
  Play,
  Popcorn,
} from "lucide-react";

export const Route = createFileRoute("/services_/cinema")({
  head: () => ({
    meta: [
      {
        title:
          "Cinema Advertising Services — KMWAdvertising · On-Screen & Lobby Media",
      },
      {
        name: "description",
        content:
          "Cinema Advertising Services That Create Powerful Brand Experiences and Unmatched Audience Engagement. Multiplex, Single Screen, On-Screen Ads & Lobby Branding by KMWA LLP.",
      },
      {
        property: "og:title",
        content: "Cinema Advertising Services — KMWAdvertising",
      },
      {
        property: "og:description",
        content:
          "Reach Captive Audiences in a Premium Entertainment Environment. Cinema media planning & buying across leading multiplex chains.",
      },
    ],
  }),
  component: CinemaServicePage,
});

export function CinemaServicePage() {
  const cinemaAdvantages = [
    {
      title: "Captive & Attentive Audience",
      desc: "Cinema audiences are present by choice and focused on the big screen before the presentation begins. This creates an ideal environment without the digital distractions of mobile devices.",
      icon: Eye,
    },
    {
      title: "Premium Audio-Visuals",
      desc: "Large-format screens, surround sound and high-definition visuals enhance advertisement effectiveness, allowing brands to showcase stories with maximum emotional impact.",
      icon: Film,
    },
    {
      title: "Strong Brand Recall",
      desc: "Research consistently proves cinema generates exceptional brand awareness and message retention due to its immersive nature and zero advertising clutter.",
      icon: Award,
    },
    {
      title: "Targeted Geographic Reach",
      desc: "Cinema campaigns can be hyper-targeted to specific cities, multiplex locations, or demographic neighbourhoods for both national and local initiatives.",
      icon: Target,
    },
    {
      title: "Integrated Campaign Synergy",
      desc: "Cinema advertising works seamlessly alongside TV, digital, radio, print and OOH campaigns to reinforce message frequency across all key consumer touchpoints.",
      icon: Sparkles,
    },
  ];

  const multiplexFormats = [
    "On-screen commercials (Pre-show & Interval)",
    "Digital cinema lobby displays",
    "Backlit lobby standees & hoardings",
    "Interactive touch kiosks",
    "Product sampling activations",
    "Ticket sleeve branding",
    "Washroom mirror branding",
    "Exclusive event sponsorships",
  ];

  const singleScreenSectors = [
    "Regional retail businesses",
    "FMCG consumer brands",
    "Educational institutions & universities",
    "Healthcare & hospital networks",
    "Automobile dealerships",
    "Real estate developers",
    "Government awareness campaigns",
    "Local events, Food & Restaurants",
  ];

  const onScreenFormats = [
    "Standard 30s/60s cinema commercials",
    "High-definition video advertisements",
    "2D and 3D commercial formats",
    "Product launch showcase films",
    "Brand storytelling documentaries",
    "Corporate profile commercials",
    "Public awareness campaigns",
    "Festival & seasonal promotions",
  ];

  const lobbyBrandingOptions = [
    "Backlit lobby displays",
    "Digital video signages",
    "Interactive kiosks",
    "Standees & cutouts",
    "Floor graphic decals",
    "Wall wraps & pillar branding",
    "Escalator & lift wraps",
    "Photo opportunities & 3D setups",
  ];

  const experientialActivations = [
    "Product demonstrations",
    "Brand sampling booths",
    "Contest activations",
    "Photo booths & AR setups",
    "Interactive games",
    "Virtual reality (VR) experiences",
    "Promotional lounge events",
    "Influencer movie screenings",
  ];

  const mediaPlanningMetrics = [
    "Age & gender demographics",
    "Household income & spending power",
    "Lifestyle & entertainment preferences",
    "Movie genre affinities (Action, Comedy, Family)",
    "Regional language preferences",
    "Cinema attendance frequency",
    "Ticket purchasing behaviour",
  ];

  const theaterSelectionCriteria = [
    "Footfall & weekly box office numbers",
    "Screen count & multiplex seating capacity",
    "Occupancy rates & show timings",
    "Audience demographic profiles",
    "Geographic location & mall integration",
    "Language & programming mix",
    "Premium seating & IMAX/3D segments",
  ];

  const cinemaBuyingServices = [
    "Multiplex media buying & screen allocation",
    "Single screen theatre bookings",
    "National cinema campaign buying",
    "Regional language cinema campaigns",
    "Premium screen placements (IMAX, 4DX)",
    "Exclusive lobby branding rights",
    "Digital screen network inventory",
    "Seasonal & blockbuster campaign packages",
    "Long-term master branding agreements",
    "Proof-of-play log reporting",
  ];

  return (
    <div className="dark relative min-h-screen bg-background text-foreground flex flex-col w-full overflow-x-clip">
      <SiteNav />

      <main className="flex-1 w-full overflow-x-clip pt-24 md:pt-28">
        {/* HERO SECTION */}
        <section className="relative overflow-hidden py-14 sm:py-20 md:py-24 border-b border-white/10">
          <div className="absolute inset-0 grid-bg opacity-15" aria-hidden />
          <div className="glow-orb -left-32 top-10 h-[450px] w-[450px] bg-[var(--glow-orange)] opacity-25" />
          <div className="glow-orb right-[-100px] top-20 h-[400px] w-[400px] bg-[var(--glow-purple)] opacity-25" />

          <div className="container-x relative z-10">
            {/* Breadcrumb */}
            <div className="flex flex-wrap items-center gap-2.5 text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">
              <Link to="/" className="hover:text-white transition">
                Home
              </Link>
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
              <Link to="/services" className="hover:text-white transition">
                Services
              </Link>
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
              <span className="text-[var(--glow-orange)] font-semibold">Cinema Advertising</span>
            </div>

            <div className="max-w-4xl">
              <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-muted-foreground border border-white/10 mb-6">
                <Film className="h-3.5 w-3.5 text-[var(--glow-orange)]" />
                Traditional Media Capabilities
              </div>

              <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.12] tracking-tight text-white">
                Cinema Advertising Services That Create Powerful Brand Experiences and Unmatched Audience Engagement
              </h1>

              <p className="mt-6 text-lg sm:text-xl font-accent italic text-[var(--glow-orange)] font-medium border-l-2 border-[var(--glow-orange)] pl-4 py-1">
                Reach Captive Audiences in a Premium Entertainment Environment
              </p>

              <div className="mt-6 space-y-4 text-base sm:text-lg text-white/90 font-normal leading-relaxed">
                <p>
                  Cinema advertising is one of the most immersive and impactful forms of traditional media. Unlike many advertising channels where audiences may skip, scroll past or ignore messages, cinema places your brand on the biggest screen in the room, capturing the full attention of viewers in a distraction-free environment.
                </p>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  The cinema experience is built around anticipation, emotion and storytelling, making it an ideal platform for brands that want to leave a lasting impression. Whether it's a blockbuster movie, a regional film release or a family entertainment weekend, cinema audiences are engaged, attentive and receptive to high-quality advertising.
                </p>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  At <strong className="text-white">KMWAdvertising LLP</strong>, we provide end-to-end cinema advertising solutions that help brands connect with audiences before, during and after the movie experience. With over two decades of experience across traditional and integrated media, we work with leading multiplex chains, independent theatres and cinema media partners to create campaigns that maximise reach and support business growth.
                </p>
              </div>

              <div className="mt-8 flex flex-wrap gap-4">
                <a href="/contact-us" className="btn-hero !py-3.5 !px-7">
                  Request Cinema Media Proposal
                  <ArrowRight className="h-4 w-4" />
                </a>
                <Link to="/services" className="btn-ghost !py-3.5 !px-7">
                  Explore All Services
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* WHY CINEMA ADVERTISING DELIVERS */}
        <section className="py-14 sm:py-20 border-b border-white/10 bg-white/[0.01]">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-orange)] block mb-2">
                Unmatched Attention
              </span>
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-white">
                Why Cinema Advertising Remains a High-Impact Marketing Channel
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground mt-3 leading-relaxed">
                Cinema offers brands a unique opportunity to communicate with audiences in a premium, high-attention environment. Unlike many other advertising formats, cinema combines sight, sound and storytelling on a large scale.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {cinemaAdvantages.map((adv, idx) => {
                const AIcon = adv.icon;
                return (
                  <div
                    key={idx}
                    className="p-6 sm:p-8 rounded-2xl glass-strong border border-white/10 hover:border-white/20 transition duration-300 flex flex-col justify-between group"
                  >
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-orange)]">
                          ADVANTAGE 0{idx + 1}
                        </span>
                        <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white">
                          <AIcon className="h-5 w-5" />
                        </div>
                      </div>
                      <h3 className="font-display text-xl font-bold text-white mb-2">
                        {adv.title}
                      </h3>
                      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                        {adv.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* OUR CINEMA ADVERTISING SERVICES */}
        <section className="py-16 md:py-24 border-b border-white/10 relative">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-orange)] block mb-2">
                End-to-End Cinema Solutions
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
                Our Cinema Advertising Services
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground mt-2">
                At KMWAdvertising LLP, we provide comprehensive cinema advertising solutions designed to align with your marketing objectives, audience profile and media budget.
              </p>
            </div>

            {/* MULTIPLEX & SINGLE SCREEN GRID */}
            <div className="grid lg:grid-cols-2 gap-8 mb-12">
              {/* MULTIPLEX */}
              <div className="p-8 sm:p-10 rounded-3xl glass-strong border border-white/15 space-y-6">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono text-[var(--glow-orange)] bg-[var(--glow-orange)]/10 border border-[var(--glow-orange)]/30 mb-3">
                    <Film className="h-3.5 w-3.5" />
                    Premium Urban Reach
                  </div>
                  <h3 className="font-display text-2xl font-bold text-white">Multiplex Advertising</h3>
                  <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider mt-1">
                    Reach Urban and Affluent Consumers
                  </p>
                </div>

                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  Multiplex cinemas attract diverse audiences, including professionals, families, students and high-spending consumers who value quality experiences.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2">
                  {multiplexFormats.map((mf, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                      <Check className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                      <span>{mf}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* SINGLE SCREEN */}
              <div className="p-8 sm:p-10 rounded-3xl glass-strong border border-white/15 space-y-6 bg-[#06060c]/90">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono text-amber-400 bg-amber-500/10 border border-amber-500/30 mb-3">
                    <Tv className="h-3.5 w-3.5" />
                    Regional Penetration
                  </div>
                  <h3 className="font-display text-2xl font-bold text-white">Single Screen Cinema</h3>
                  <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider mt-1">
                    Build Strong Regional Brand Presence
                  </p>
                </div>

                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  Single screen theatres continue to play an important role in regional and semi-urban markets, offering cost-effective local reach.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2">
                  {singleScreenSectors.map((ss, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                      <Check className="h-3.5 w-3.5 text-amber-400 shrink-0" />
                      <span>{ss}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ON-SCREEN, LOBBY & EXPERIENTIAL GRID */}
            <div className="grid lg:grid-cols-3 gap-6 mb-16">
              {/* ON-SCREEN */}
              <div className="p-6 rounded-2xl glass-strong border border-white/10 space-y-4">
                <h4 className="font-display text-xl font-bold text-white flex items-center gap-2">
                  <Play className="h-4 w-4 text-[var(--glow-orange)]" />
                  On-Screen Ads
                </h4>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Deliver your message on the biggest screen before the feature presentation:
                </p>
                <div className="space-y-1.5">
                  {onScreenFormats.map((of, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                      <Check className="h-3 w-3 text-emerald-400 shrink-0" />
                      <span>{of}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* LOBBY */}
              <div className="p-6 rounded-2xl glass-strong border border-white/10 space-y-4">
                <h4 className="font-display text-xl font-bold text-white flex items-center gap-2">
                  <Maximize2 className="h-4 w-4 text-[var(--glow-orange)]" />
                  Cinema Lobby Branding
                </h4>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Extend engagement during ticket purchase and concessions wait times:
                </p>
                <div className="space-y-1.5">
                  {lobbyBrandingOptions.map((lb, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                      <Check className="h-3 w-3 text-emerald-400 shrink-0" />
                      <span>{lb}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* EXPERIENTIAL */}
              <div className="p-6 rounded-2xl glass-strong border border-white/10 space-y-4">
                <h4 className="font-display text-xl font-bold text-white flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-[var(--glow-orange)]" />
                  Experiential Activations
                </h4>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Create memorable brand interactions and social sharing:
                </p>
                <div className="space-y-1.5">
                  {experientialActivations.map((ea, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                      <Check className="h-3 w-3 text-emerald-400 shrink-0" />
                      <span>{ea}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* CINEMA MEDIA PLANNING & BUYING */}
            <div className="grid lg:grid-cols-2 gap-8">
              {/* PLANNING */}
              <div className="p-8 sm:p-10 rounded-3xl glass-strong border border-white/15 space-y-6">
                <div>
                  <h3 className="font-display text-2xl font-bold text-white flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-[var(--glow-orange)]" />
                    Cinema Media Planning
                  </h3>
                  <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider mt-1">
                    Strategic Planning & Blockbuster Release Scheduling
                  </p>
                </div>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  Successful cinema advertising starts with understanding who you want to reach and planning around major blockbuster movie releases, holiday periods and regional film launches.
                </p>

                <div>
                  <h5 className="text-xs font-mono uppercase tracking-widest text-white/80 mb-3 block">
                    Audience & Theatre Selection Factors:
                  </h5>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {theaterSelectionCriteria.map((tc, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                        <Check className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                        <span>{tc}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* BUYING */}
              <div className="p-8 sm:p-10 rounded-3xl glass-strong border border-white/15 space-y-6 bg-[#06060c]/90">
                <div>
                  <h3 className="font-display text-2xl font-bold text-white flex items-center gap-2">
                    <ShieldCheck className="h-5 w-5 text-[var(--glow-orange)]" />
                    Cinema Media Buying
                  </h3>
                  <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider mt-1">
                    Secure Premium Cinema Inventory at Competitive Rates
                  </p>
                </div>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  Our specialists negotiate directly with cinema chains, multiplex operators and media owners to secure high-quality screen inventory while optimising budgets.
                </p>

                <div>
                  <h5 className="text-xs font-mono uppercase tracking-widest text-white/80 mb-3 block">
                    Our Buying Scope Includes:
                  </h5>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {cinemaBuyingServices.map((cbs, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                        <Check className="h-3.5 w-3.5 text-[var(--glow-orange)] shrink-0" />
                        <span>{cbs}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CLOSING CTA BANNER */}
        <section className="py-20 md:py-28 relative overflow-hidden">
          <div className="glow-orb left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[500px] w-[500px] bg-[var(--glow-orange)] opacity-20" />

          <div className="container-x relative z-10 text-center max-w-4xl">
            <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-xs uppercase tracking-widest text-muted-foreground border border-white/10 mb-6">
              <Film className="h-3.5 w-3.5 text-[var(--glow-orange)]" />
              Cinema Media Advisory
            </div>

            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-tight">
              Ready to Engage Captive Audiences with Cinema Advertising?
            </h2>

            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Connect with KMWAdvertising LLP's cinema media buying and planning specialists to build high-impact movie theatre campaigns tailored to your target audience.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="/contact-us" className="btn-hero w-full sm:w-auto">
                Request Cinema Proposal
                <ArrowRight className="h-4 w-4" />
              </a>
              <Link to="/services" className="btn-ghost w-full sm:w-auto">
                Back to All Services
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
